import { useCallback, useEffect, useMemo, useRef } from "react";
import api from "../services/api";

const DEFAULT_DELAY = 800;

/**
 * Debounced writer for a single task's fields.
 *
 * The task surfaces used to hold one `debounce((id, field, value) => …)` per
 * component. Because lodash's debounce keeps only the *last* invocation, two
 * edits to different fields inside the debounce window collapsed into one and
 * the earlier field was silently dropped — pick a status and then a sprint in
 * quick succession and only the sprint was ever sent.
 *
 * This accumulates edits into a single pending patch instead, so every field
 * survives and the whole batch goes out in one request.
 *
 * It also tracks which fields are still unsaved, which lets callers merge a
 * PATCH response without clobbering edits the user made while it was in flight.
 *
 * @param {Object} params
 * @param {number|string} params.taskId
 * @param {number} params.workspaceId
 * @param {(data: Object) => void} [params.onPatched] Server representation of
 *   the saved task, for callers that want to merge derived fields back in.
 * @param {(error: any, updates: Object) => void} [params.onError] Receives the
 *   batch that failed, so the caller can report it and re-read the task.
 * @param {(pending: boolean) => void} [params.onPendingChange]
 * @param {number} [params.delay]
 */
export default function useTaskFieldPatcher({
  taskId,
  workspaceId,
  onPatched,
  onError,
  onPendingChange,
  delay = DEFAULT_DELAY,
}) {
  const pendingRef = useRef({});
  const timerRef = useRef(null);
  // Fields written since the in-flight request was sent. The response is stale
  // with respect to these, so a caller merging it must keep its local value.
  const inFlightFieldsRef = useRef(new Set());

  // Keep the latest props in refs so `flush`/`patchField` stay referentially
  // stable — an unstable writer would re-arm effects on every render.
  const cfgRef = useRef({});
  cfgRef.current = { taskId, workspaceId, onPatched, onError, onPendingChange };

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const updates = pendingRef.current;
    pendingRef.current = {};
    const fields = Object.keys(updates);
    if (fields.length === 0) return;

    const { taskId: id, workspaceId: ws, onPatched: done, onError: fail } =
      cfgRef.current;
    if (!id) return;

    fields.forEach((f) => inFlightFieldsRef.current.add(f));
    try {
      const response = await api.patch(`tasks/${id}/`, {
        ...updates,
        workspace: ws,
      });
      fields.forEach((f) => inFlightFieldsRef.current.delete(f));
      done?.(response.data);
    } catch (error) {
      fields.forEach((f) => inFlightFieldsRef.current.delete(f));
      console.error("Error updating task:", error, { taskId: id, updates });
      fail?.(error, updates);
    } finally {
      if (
        Object.keys(pendingRef.current).length === 0 &&
        inFlightFieldsRef.current.size === 0
      ) {
        cfgRef.current.onPendingChange?.(false);
      }
    }
  }, []);

  const patchField = useCallback(
    (field, value) => {
      pendingRef.current[field] = value;
      cfgRef.current.onPendingChange?.(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, delay);
    },
    [flush, delay]
  );

  /** Fields whose local value is newer than any response now in flight. */
  const unsavedFields = useCallback(
    () =>
      new Set([
        ...Object.keys(pendingRef.current),
        ...inFlightFieldsRef.current,
      ]),
    []
  );

  /**
   * Merge a server task representation into local state, preserving fields the
   * user has edited since the request went out.
   */
  const mergeResponse = useCallback(
    (prev, data) => {
      if (!prev) return data;
      const keep = unsavedFields();
      const merged = { ...prev, ...data };
      keep.forEach((field) => {
        if (field in prev) merged[field] = prev[field];
      });
      return merged;
    },
    [unsavedFields]
  );

  // Send whatever is still pending when the surface unmounts (drawer closed,
  // navigated away) so the last keystrokes are not lost.
  useEffect(() => () => { flush(); }, [flush]);

  return useMemo(
    () => ({ patchField, flush, mergeResponse, unsavedFields }),
    [patchField, flush, mergeResponse, unsavedFields]
  );
}
